/* eslint-disable no-param-reassign */
import { useNavigate } from "react-router-dom";
import { useCallback, useContext, useEffect, useState } from "react";
import { Box, IconButton, SxProps, TableRow, Typography, Tooltip, SpeedDial } from "@mui/material";
import { SuccessAlertContext } from "App";
import CloseIcon from "@mui/icons-material/Close";
import { checkAccessTokenExpired } from "util/auth";
import ERROR_MESSAGE from "constants/message";
import Notify from "config/notify";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { AmountHiddenSymbol, Category, StoreSearchApiParams, StoreStock, StoreStockSet, StoreStockSingle, StoreStockSingleInSet } from "../types";
import StockTable from "../components/StockTable";
import StockSearchDialog from "../components/StockSearchDialog";
import StockContainer from "../components/StockContainer";
import useStockSearch from "../hooks/useStockSearch";
import useStoreStockList from "../hooks/useStoreStockList";
import TableCellSmall, { MultiTypography, TableCellNarrow, TableCellWide, TableHeaderCellSmall } from "../components/TableCellSmall";
import { categories, supplierBackgroundColor, supplierHeaderColor } from "../constants";
import { extractNoDupplicationSupplier, localeStringDisZero, removeEmptyValues } from "../utils/common";
import CustomTooltip from "../components/CustomTooltip";

const limit = 10;

const styles: {[key: string]: SxProps} = {
  supplierTopCol: {
    textAlign: 'center',
    p: 0.5,
    backgroundColor: supplierHeaderColor,
    borderBottom: 'none',
  },
  supplierBottomCol: {
    textAlign: 'center',
    p: 0.5,
    top: 33,
    backgroundColor: supplierHeaderColor,
  },
};

// 隠すことのできるカラム名
const toggleVisibleColumns = ['商品コード', 'JANコード', '大分類', '仕入先名', 'ブランド', '商品名', '品番', 'サイズ'];
// 取り除くカラムを設定(タイヤ・ホイールの場合は空)
const generalInvisibleColumnIndices: number[] = [1,2,4,7];
const tyreWheelInvisibleColumnIndices: number[] = [1,2,6];
const defaultInvisibleColumnIndices = (c: Category) => c === categories[1] ? generalInvisibleColumnIndices : tyreWheelInvisibleColumnIndices;

/**
 * 店舗用在庫一覧画面
 * @returns JSX.Element
 */
export default function StoreStockList() {
  const isExistCd = localStorage.getItem('shop_cd');
  const { showAlert } = useContext(SuccessAlertContext);
  const [isLoading, setIsLoading] = useState(false);
  const [{total, stockList, isLoading: isStockLoading, isMoreLoading: isMoreStockLoading, isFastLoading, fireMoreLoadEvent, storeUpdated, storehouseUpdated}, {get: getStockList, getMore: getMoreStockList, setFireMoreLoadEvent, setInit}] = useStoreStockList(showAlert);
  const [isOpenStockSearch, setIsOpenStockSearch] = useState(true);
  const [
    {productCd, productName, brand, JANCd, productNumber, tyreWidth, tyreRatio, tyreRimDiam, wheelWidth, wheelPcd, wheelHole, wheelRimDiam, classification, category, supplier},
    {setProductCd, setProductName, setBrand, setJANCd, setProductNumber, setTyreWidth, setTyreRatio, setTyreRimDiam, setWheelWidth, setWheelPcd, setWheelHole, setWheelRimDiam, setClassification, setCategory, setSupplier},
    {classificationOptions, tyreOptions, wheelOptions, brandOptions, shopOptions},
    {isLoading: isOptionsLoading}
  ] = useStockSearch(true);
  const [filteredCategory, setFilteredCategory] = useState<Category>(category);

  const navigate = useNavigate();
  const [invisibleColumnIndices, setInvisibleColumnIndices] = useState<number[]>(defaultInvisibleColumnIndices(category));

  const [isScrollTop, setIsScrollTop] = useState(false);
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  // useStockSearchに含むべきだが、後付けで店舗用のみなのでここに書いた。0倉庫、0店舗数量非表示対応
  const [isHideStorehouseZero, setIsHideStorehouseZero] = useState(false);
  const [isHideStoreZero, setIsHideStoreZero] = useState(false)

  // 単品かセットか
  const checkProductSet = useCallback((stock: StoreStock): stock is StoreStockSet => 'productSetCd' in stock, []);
  const calcZeroOption = useCallback(() => {
    let cost = 0;
    if (isHideStoreZero) cost  += 1;
    if (isHideStorehouseZero) cost += 2;
    return cost;
  }, [isHideStoreZero, isHideStorehouseZero])

  // 現在の検索条件にあるデータを基にAPIのパラメータを作成
  const createGetListApiParams = (): StoreSearchApiParams => {
    const params: Omit<StoreSearchApiParams, 'limit' | 'offset'> = {
      item_cd: productCd,
      item_name: productName,
      brand: brand ?? "",
      jan_cd: JANCd,
      item_num: productNumber,
      tire_width: classification === "タイヤ" ? tyreWidth : undefined,
      tire_ratio: classification === "タイヤ" ? tyreRatio : undefined,
      tire_rim_size: classification === "タイヤ" ? tyreRimDiam : undefined,
      wheel_width: classification === "ホイール" ? wheelWidth : undefined,
      wheel_pcd: classification === "ホイール" ? wheelPcd : undefined,
      wheel_hole: classification === "ホイール" ? wheelHole : undefined,
      wheel_rim_size: classification === "ホイール" ? wheelRimDiam : undefined,
      class_cd: classificationOptions.some(co => co.name === classification) ? classificationOptions.filter(co => co.name === classification)[0].id : '',
      vendor_cd: supplier ? extractNoDupplicationSupplier(classificationOptions).filter(so => so.supplierName === supplier)[0].supplierCd : '',
      zero_option: calcZeroOption(),
    };
    return removeEmptyValues(params);
  };

  const onSearchClick = async () => {
    setIsOpenStockSearch(false);
    await getStockList(createGetListApiParams());
    // 表示するカラムのリセット
    setInvisibleColumnIndices(defaultInvisibleColumnIndices(category));
    setFilteredCategory(category);
  };

  const handleToggleOpenColumn = useCallback((index: number) => {
    setInvisibleColumnIndices(prev => {
      const newIndices = [...prev]
      if (!prev.includes(index)) {
        newIndices.push(index);
        return newIndices;
      }
      return prev;
    })
  }, []);

  const handleVisibleColumn = useCallback((index: number) => {
    setInvisibleColumnIndices(prev =>{
      const newIndices = [...prev];
      newIndices.splice(index, 1);
      return newIndices;
    });
  }, []);

  const isCategoryGeneral = useCallback(() => filteredCategory === categories[1], [filteredCategory]);

  const handleBack = () => navigate('/');

  const checkHiddenSupplier = (sDate: string, nAmount: number | AmountHiddenSymbol, cAmount: number | AmountHiddenSymbol) => {
    if (nAmount === '-' && cAmount === '-' || !sDate) return '-'
    return sDate;
  }

  // 単品のセル
  const clickableSingleCell = useCallback((stock: StoreStockSingle, show: boolean | undefined) => (
    ( show ?
      <TableRow
        key={stock.id}
      >
        {[
          <TableCellSmall key="product_cd">{stock.isDiscontinued ? <CustomTooltip title="*がついているものは廃番です。" arrow><Box sx={{cursor: "pointer"}}>{<Typography component="span" sx={{color: 'red'}}>*</Typography>}{stock.productCd}</Box></CustomTooltip> : stock.productCd}</TableCellSmall>,
          <TableCellSmall key="jan_cd">{stock.JANCd}</TableCellSmall>,
          <TableCellWide key="classification"><MultiTypography>{classificationOptions.filter((c) => c.id === stock.classification).length > 0 ? classificationOptions.filter((c) => c.id === stock.classification)[0].name : stock.classification}</MultiTypography></TableCellWide>,
          <TableCellWide key="supplier_name" sx={{backgroundColor: supplierBackgroundColor}}><MultiTypography>{stock.supplierName}</MultiTypography></TableCellWide>,
          <TableCellSmall key="brand"><MultiTypography>{stock.brand ?? ''}</MultiTypography></TableCellSmall>,
          <TableCellWide key="product_name"><MultiTypography>{stock.productName}</MultiTypography></TableCellWide>,
          <TableCellSmall key="product_number">{stock.productNumber}</TableCellSmall>,
          <TableCellSmall key="size">{stock.size ?? null}</TableCellSmall>,
        ].filter((_,index) => !invisibleColumnIndices.includes(index))}
        <TableCellSmall sx={{textAlign: "right"}}>{stock.isStagnantStock ? <CustomTooltip title="※がついているものは滞留在庫です。" arrow><Box sx={{cursor: "pointer"}}>{<Typography component="span" sx={{color: 'red', fontSize: 14}}>※</Typography>}{stock.storeAmount.toLocaleString()}</Box></CustomTooltip> : stock.storeAmount.toLocaleString()}</TableCellSmall>
        {/* タイヤ・ホイールの場合のみならず、一般商品の場合も物流の在庫数量がここに入る  */}
        <TableCellSmall sx={{textAlign: "right"}}>{localeStringDisZero(stock.tyreStorehouse)}</TableCellSmall>
        <TableCellSmall sx={{textAlign: Number(stock.neighborAmount) ? "right" : "center", backgroundColor: supplierBackgroundColor}}>{stock.neighborAmount.toLocaleString()}</TableCellSmall>
        <TableCellSmall sx={{textAlign: Number(stock.centerAmount) ? "right" : "center", backgroundColor: supplierBackgroundColor}}>{stock.centerAmount.toLocaleString()}</TableCellSmall>
        <TableCellSmall sx={{backgroundColor: supplierBackgroundColor}}>{checkHiddenSupplier(stock.arrivalDate, stock.neighborAmount, stock.centerAmount)}</TableCellSmall>
        <TableCellNarrow sx={{backgroundColor: supplierBackgroundColor, maxWidth: 60}}>{checkHiddenSupplier(stock.updateTime, stock.neighborAmount, stock.centerAmount)}</TableCellNarrow>
      </TableRow>
      // eslint-disable-next-line react/jsx-no-useless-fragment
      : <></>
    )
  ), [classificationOptions, invisibleColumnIndices]);

  // 混合のセル
  const fieldCell = useCallback((stock: StoreStock) => {
    let ele: JSX.Element;
    if (checkProductSet(stock)) {
      // セット
      ele =
        <TableRow key={stock.id}>
          {
            [
              <TableCellSmall key="product_set_cd"><Box display="flex" alignItems="center">{stock.isDiscontinued ? <CustomTooltip title="*がついているものは廃番です。" arrow><Box sx={{cursor: "pointer"}}>{<Typography component="span" sx={{color: 'red'}}>*</Typography>}{stock.productSetCd}</Box></CustomTooltip> : stock.productSetCd}</Box></TableCellSmall>,
              <TableCellSmall key="jan_cd">
                {(() => (
                  <>
                    <Box marginBottom={1}>
                      <MultiTypography>{stock.JANCd.split('+')[0]}</MultiTypography>
                    </Box>
                    <Box>
                      <MultiTypography>{stock.JANCd.split('+')[1]}</MultiTypography>
                    </Box>
                  </>
                ))()}
              </TableCellSmall>,
              <TableCellSmall key="classification">{classificationOptions.filter((c) => c.id === stock.classification).length > 0 ? classificationOptions.filter((c) => c.id === stock.classification)[0].name : stock.classification}</TableCellSmall>,
              <TableCellSmall key="supplier_name" sx={{backgroundColor: supplierBackgroundColor}}>-</TableCellSmall>,
              <TableCellSmall key="brand"><Box marginBottom={1}><MultiTypography>{stock.stockSingleList[0].brand}</MultiTypography></Box><Box><MultiTypography>{stock.stockSingleList[1].brand}</MultiTypography></Box></TableCellSmall>,
              <TableCellSmall key="product_name"><Box marginBottom={1}><MultiTypography>{stock.stockSingleList[0].productName}</MultiTypography></Box><Box><MultiTypography>{stock.stockSingleList[1].productName}</MultiTypography></Box></TableCellSmall>,
              <TableCellSmall key="product_number">
                {(() => (
                  <>
                    <Box marginBottom={1}>
                      <MultiTypography>{stock.productNumber.split('+')[0]}</MultiTypography>
                    </Box>
                    <Box>
                      <MultiTypography>{stock.productNumber.split('+')[1]}</MultiTypography>
                    </Box>
                  </>
                ))()}
              </TableCellSmall>,
              <TableCellSmall key="size"><Box marginBottom={1}>{stock.stockSingleList[0].size}</Box><Box>{stock.stockSingleList[1].size}</Box></TableCellSmall>,
            ].filter((_,index) => !invisibleColumnIndices.includes(index))
          }
          <TableCellSmall sx={{textAlign: "right"}}>{stock.isStagnantStock ? <CustomTooltip title="※がついているものは滞留在庫です。" arrow><Box sx={{cursor: "pointer"}}>{<Typography component="span" sx={{color: 'red', fontSize: 14}}>※</Typography>}{stock.storeAmount.toLocaleString()}</Box></CustomTooltip> : stock.storeAmount.toLocaleString()}</TableCellSmall>
          <TableCellSmall sx={{textAlign: "right"}}>{localeStringDisZero(stock.tyreStorehouse)}</TableCellSmall>
          <TableCellSmall sx={{backgroundColor: supplierBackgroundColor}}>-</TableCellSmall>
          <TableCellSmall sx={{backgroundColor: supplierBackgroundColor}}>-</TableCellSmall>
          <TableCellSmall sx={{backgroundColor: supplierBackgroundColor}}>-</TableCellSmall>
          <TableCellNarrow sx={{backgroundColor: supplierBackgroundColor, maxWidth: 60}}>-</TableCellNarrow>
        </TableRow>
    } else {
      // 単品
      ele =
        clickableSingleCell(stock, true);
    }
    return ele;
  }, [clickableSingleCell, checkProductSet, classificationOptions, invisibleColumnIndices]);

  const iconButton = useCallback((index: number, colName: string) => (
    <Box width="100%">
      <Box width="fit-content" position="relative">
        {colName}
        <Box position="absolute" top={-20} right={10} width={0} height="fit-content" alignContent="center">
          <IconButton onClick={() => handleToggleOpenColumn(index)}><CloseIcon sx={{fontSize: 14}} /></IconButton>
        </Box>
      </Box>
    </Box>
  ), [handleToggleOpenColumn]);

  const tableHeadElement = useCallback(() => (
    <>
      <TableRow>
        {[
          <TableHeaderCellSmall key="product_cd" rowSpan={2}>商品コード</TableHeaderCellSmall>,
          <TableHeaderCellSmall key="jan_cd" rowSpan={2}>{iconButton(1, 'JANコード')}</TableHeaderCellSmall>,
          <TableHeaderCellSmall key="classification" rowSpan={2}>{iconButton(2, '大分類')}</TableHeaderCellSmall>,
          <TableHeaderCellSmall key="supplier_name" rowSpan={2} sx={{backgroundColor: supplierHeaderColor}}>{iconButton(3, '仕入先名')}</TableHeaderCellSmall>,
          <TableHeaderCellSmall key="brand" rowSpan={2}>{iconButton(4, 'ブランド')}</TableHeaderCellSmall>,
          <TableHeaderCellSmall key="product_name" rowSpan={2}>{iconButton(5, '商品名')}</TableHeaderCellSmall>,
          <TableHeaderCellSmall key="product_number" rowSpan={2}>{iconButton(6, '品番')}</TableHeaderCellSmall>,
          <TableHeaderCellSmall key="size" rowSpan={2}>{iconButton(7, 'サイズ')}</TableHeaderCellSmall>,
        ].filter((_,index) => !invisibleColumnIndices.includes(index))}
        <TableHeaderCellSmall rowSpan={2}>店舗数量</TableHeaderCellSmall>
        { isCategoryGeneral()
          ?
            <TableHeaderCellSmall rowSpan={2} sx={{py: 0}}><MultiTypography sx={{textAlign: 'center'}}><>物流センター<br />数量</></MultiTypography></TableHeaderCellSmall>
          :
            <TableHeaderCellSmall rowSpan={2} sx={{py: 0}}><MultiTypography sx={{textAlign: 'center'}}><>タイヤYH<br />倉庫数量</></MultiTypography></TableHeaderCellSmall>
        }
        <TableHeaderCellSmall colSpan={4} sx={{textAlign: "center", backgroundColor: supplierHeaderColor, ...styles.supplierTopCol}}>仕入先</TableHeaderCellSmall>
      </TableRow>
      <TableRow>
        <TableHeaderCellSmall sx={styles.supplierBottomCol}>近隣倉庫数量</TableHeaderCellSmall>
        <TableHeaderCellSmall sx={{...styles.supplierBottomCol, py: 0}}><MultiTypography sx={{textAlign: 'center'}}><>センター<br />倉庫数量</></MultiTypography></TableHeaderCellSmall>
        <TableHeaderCellSmall sx={styles.supplierBottomCol}>入荷予定日</TableHeaderCellSmall>
        <TableHeaderCellSmall sx={styles.supplierBottomCol}>更新日時</TableHeaderCellSmall>
      </TableRow>
    </>
  ), [iconButton, isCategoryGeneral, invisibleColumnIndices]);

  const tableBodyElement = useCallback(() => (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {stockList.length > 0 && stockList.map((stock) => (
        fieldCell(stock)
      ))}
    </>
  ), [stockList, fieldCell]);

  // スクロール処理
  const handleScroll = async () => {
    setIsScrollTop(window.pageYOffset > 0);

    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading.current) {
      return;
    }
    const scrollTop = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    if (scrollTop + windowHeight + 10 >= documentHeight) {
      setFireMoreLoadEvent(prev => !prev);
    }
  };

  const checkScrollable = () => document.documentElement.scrollHeight > window.innerHeight;

  // 中のコンテンツが小さいとスクロールできず、無限ローディングさせることができないため、事前に+αで読んでおく。
  useEffect(() => {
    if (limit <= stockList.length && stockList.length < total && !isLoading && !isFastLoading.current) {
      if (!checkScrollable()) {
        (async() => {
          await getMoreStockList(createGetListApiParams());
        })();
      }
    }
  }, [stockList, isLoading]);

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async() => {
      await getMoreStockList(createGetListApiParams());
    })();
  }, [fireMoreLoadEvent]);

  useEffect(() => {
    setTimeout(() => {
      (async () => {
        if (!isExistCd) {
          setIsOpenStockSearch(false);
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: "店舗コードが設定されていません。再度ログインしてください。",
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        } else if (checkAccessTokenExpired()) {
          // 期限切れなら
          setIsOpenStockSearch(false);
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        }
        setInit(true);
      })();
      window.addEventListener('scroll', handleScroll);
    }, 0);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    // loadingの統一
    setIsLoading(isOptionsLoading || isStockLoading);
  }, [isOptionsLoading, isStockLoading]);

  return (
    <Box>
      <StockContainer stockTitle="在庫情報一覧" StockSearch={
        <StockSearchDialog
          open={isOpenStockSearch}
          onClose={() => setIsOpenStockSearch(false)}
          onSearch={onSearchClick}
          productCd={productCd}
          productName={productName}
          brand={brand}
          JANCd={JANCd}
          productNumber={productNumber}
          tyreWidth={tyreWidth}
          tyreRatio={tyreRatio}
          tyreRimDiam={tyreRimDiam}
          wheelWidth={wheelWidth}
          wheelPcd={wheelPcd}
          wheelHole={wheelHole}
          wheelRimDiam={wheelRimDiam}
          classification={classification}
          supplier={supplier}
          isHideStoreZero={isHideStoreZero}
          isHideStorehouseZero={isHideStorehouseZero}
          classificationOptions={classificationOptions}
          tyreOptions={tyreOptions}
          wheelOptions={wheelOptions}
          brandOptions={brandOptions}
          shopOptions={shopOptions}
          // supplierOptions={supplierOptions}
          setProductCd={setProductCd}
          setProductName={setProductName}
          setBrand={setBrand}
          setJANCd={setJANCd}
          setProductNumber={setProductNumber}
          setTyreWidth={setTyreWidth}
          setTyreRatio={setTyreRatio}
          setTyreRimDiam={setTyreRimDiam}
          setWheelWidth={setWheelWidth}
          setWheelPcd={setWheelPcd}
          setWheelHole={setWheelHole}
          setWheelRimDiam={setWheelRimDiam}
          setClassification={setClassification}
          setCategory={setCategory}
          setSupplier={setSupplier}
          setIsHideStoreZero={setIsHideStoreZero}
          setIsHideStorehouseZero={setIsHideStorehouseZero}
          isStoreStockScreen
        />}
        StockTable={<StockTable searchLen={total} tableHeadElement={tableHeadElement} tableBodyElement={tableBodyElement} isAllLoaded={stockList.length >= total} isLoading={isMoreStockLoading} storeUpdated={storeUpdated} storehouseUpdated={storehouseUpdated} toggleVisibleColumns={toggleVisibleColumns} invisibleColumnIndices={invisibleColumnIndices} handleVisibleColumn={handleVisibleColumn} />}
        setIsOpenStockSearch={setIsOpenStockSearch}
        isLoading={isLoading}
        onBack={handleBack}
        manualName="manual_store"
      />
      {isScrollTop &&
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <SpeedDial
            ariaLabel="Back to top"
            icon={<KeyboardArrowUpIcon />}
            onClick={scrollToTop}
            open             // 常に展開済みにしてアイコンだけ表示
            direction="up"          // 展開方向は上だがActionは不要
            FabProps={{ size: 'small' }}
            />
        </Box>
      }
    </Box>
  );
}